import { APIPaginatedRequest, Attachment } from "shared/api/api";
import { Photo } from "shared/api/types";
import { Sort } from "shared/types";

import { AnyDateDropdownOptions } from "pages/shared/Dashboard/types";

import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import {
  DashboardPageNode,
  MobileRecordPageNode,
  RowNode,
} from "features/ui/PageBuilder/types";

export type TableType = "event" | "asset" | "catalog";

export enum TableTypeEnum {
  EVENT = "event",
  ASSET = "asset",
  CATALOG = "catalog",
}

export type ColumnDataType =
  | "string"
  | "int"
  | "float"
  | "boolean"
  | "date"
  | "datetime"
  | "datetime_in_utc"
  | "json"
  | "string_array";

export interface Column {
  ID: string;
  schemaDefinitionID: string;
  name: string;
  dataType: ColumnDataType;
  position: number;
  isPrimaryKey: boolean;
  isRequired: boolean;
  orderByPosition: number | null;
  orderByExpr: string | null;
  partitionPosition: number | null;
  partitionExpr: string | null;
  isLowCardinality: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AssociatedEventConfig {
  associatedSchemaName: string;
  associatedDateField: string;
}

export interface SchemaAnalyticsConfig {
  assetName: string;
  dateField: string;
  assetBirthdayField: string;
  costField?: string;
  associatedEvents?: AssociatedEventConfig[];
  // Offers the smart-groups table as an alternative view of the events table
  // on the schema's analytics page.
  enableSmartGroups?: boolean;
}

/**
 * Groups the view's events by the values of one event attribute. Mutually exclusive with
 * `enableSmartGroups` — a view offers one kind of grouping or none.
 */
export interface SchemaDashboardAttributeGrouping {
  attribute: string;
  /** Defaults to the attribute's display name. */
  label?: string;
}

export interface SchemaDashboardView {
  versionKey: string;
  title: string;
  dashboardOrder?: number;
  columnAccessors: string[];
  smartGroupColumnAccessors?: string[];
  enableSmartGroups?: boolean;
  attributeGrouping?: SchemaDashboardAttributeGrouping;
  // Drops the all-time comparison from the smart-groups table (and the request),
  // so the server never builds the all-time subqueries.
  simplified?: boolean;
  // Overrides analyticsConfig.dateField for this view's smart-group chart;
  // falls back to it when unset.
  chartDateField?: string;
  // Hides the view's dashboard tab from everyone but super admins.
  beta?: boolean;
  filters: SchemaDashboardFiltersConfig;
  // PageBuilder dashboard nodes (metric cards etc.) rendered between the
  // filters and the table on the SchemaDashboard view.
  cards?: RowNode<DashboardPageNode>[];
  filterSummaryHiddenFields?: string[];
}

// DETAILS PAGE CONFIG

export interface SchemaDetailsPageConfig {
  titleField?: string;
  titleRowFields?: string[];
  children?: RowNode[];
}

export interface SchemaMobileDetailsPageConfig {
  titleField?: string;
  // An attribute with chip colors; the app header takes the color of the rule
  // that matches its value.
  headerColorField?: string;
  children?: MobileRecordPageNode[];
}

// MOBILE LISTING PAGE CONFIG

export type MobileListingLayout = "table" | "card";

// Each corner holds a comma-separated accessor list ("facility,machine");
// configs saved before multi-attribute support hold a single accessor, which
// is the same format with one entry. The string shape is deliberate: mobile
// app builds that predate multi-attribute support parse corners as strings,
// so a multi-accessor config degrades to an empty corner there instead of
// dropping the whole card layout.
export interface SchemaMobileListingCardConfig {
  topLeft?: string;
  topRight?: string;
  bottomLeft?: string;
  bottomRight?: string;
}

export interface SchemaMobileListingPageConfig {
  layout?: MobileListingLayout;
  // Attribute accessors shown in each card corner; only used when layout is
  // "card".
  cardConfig?: SchemaMobileListingCardConfig;
}

// TITLE CARD CONFIG

// Each slot holds one attribute accessor. Clients clamp the header to 1 line,
// the title, title right and subtitle to 2 lines, and info fields to 3 lines.
export interface TitleCardConfig {
  headerLeft?: string;
  headerRight?: string;
  title?: string;
  titleRight?: string;
  subtitle?: string;
  showSubtitleLabel?: boolean;
  infoFields?: TitleCardInfoFieldConfig[];
}

export interface TitleCardInfoFieldConfig {
  attribute: string;
  showLabel: boolean;
}

// MOBILE DASHBOARDS CONFIG

export type MobileDashboardGrouping = "daily" | "weekly" | "monthly";

// The app shows the table when titleCard has no title.
export interface SchemaMobileDashboardLayout {
  layout?: MobileListingLayout;
  titleCard?: TitleCardConfig;
}

export interface SchemaMobileDashboardSort extends SchemaDefaultSort {
  // Only for a date-like column.
  grouping?: MobileDashboardGrouping;
}

export interface SchemaMobileDashboardConfig {
  versionKey: string;
  title: string;
  dashboardOrder?: number;
  // Hides the dashboard from everyone but super admins.
  beta?: boolean;
  // Shows the dashboard as its own app tab instead of in the dashboard dropdown.
  showAsTab?: boolean;
  // Accessor of a string or boolean attribute.
  primaryFilter?: string;
  filterDefinitions: FilterDefinition[];
  staticFilters?: FilterGroupState;
  staticAssetFilters?: FilterGroupState;
  // Falls back to the schema's config.defaultSort when unset.
  sort?: SchemaMobileDashboardSort;
  layout: SchemaMobileDashboardLayout;
}

export interface SchemaMobileDashboardsConfig {
  dashboards: SchemaMobileDashboardConfig[];
}

// FILTERS

export interface SchemaDashboardFiltersConfig {
  staticEventFilters: FilterGroupState;
  staticAssetFilters: FilterGroupState;
  filterDefinitions?: FilterDefinition[];
}

export enum FilterDefinitionType {
  DATE = "date",
  QUICK_FILTER = "quick_filter",
  IS_INCLUDED_IN = "is_included_in",
  KEYWORD_SEARCH = "keyword_search",
}

interface BaseFilterDefinition {
  id: string;
  schemaType: TableTypeEnum;
  label?: string;
}

/**
 * Simple filter definitions (filters that show on top of the dashboard) can be routed to
 * the smart-groups endpoint's current and/or comparison filter slots. Absent
 * fields are treated as `true` so already-persisted definitions keep working.
 *
 * `showFilter` controls toolbar visibility only — when `false`, the filter is
 * not rendered in the dashboard but its current value still merges into the
 * outgoing query (useful for "locked" defaults the user can't change).
 */
export interface SimpleFilterDefinition extends BaseFilterDefinition {
  isFilter?: boolean;
  isComparisonFilter?: boolean;
  showFilter?: boolean;
}

export interface DateFilterDefinition extends SimpleFilterDefinition {
  type: FilterDefinitionType.DATE;
  dateField: string;
  dateFieldDropdownOptions: AnyDateDropdownOptions[];
  defaultDateOption?: AnyDateDropdownOptions;
  /**
   * Marks this date filter's currently-selected value as the source for the
   * "standard" date filter.
   */
  isStandardDateFilter?: boolean;
  /**
   * Marks this date filter's currently-selected value as the source for the
   * "comparison" date filter.
   */
  isComparisonDateFilter?: boolean;
}

export interface QuickFilterDefinition extends SimpleFilterDefinition {
  type: FilterDefinitionType.QUICK_FILTER;
  defaultFilter?: FilterRowState[];
  isMultiSelect?: boolean;
}

export interface IsIncludedInFilterDefinition extends BaseFilterDefinition {
  type: FilterDefinitionType.IS_INCLUDED_IN;
  /** ID of the one-to-many related schema definition on the parent schema. */
  relatedSchemaID: string;
  /** Filter definitions evaluated against the related schema. */
  nestedFilterDefinitions?: FilterDefinition[];
}

export interface KeywordSearchFilterDefinition extends SimpleFilterDefinition {
  type: FilterDefinitionType.KEYWORD_SEARCH;
}

export type FilterDefinition =
  | DateFilterDefinition
  | QuickFilterDefinition
  | IsIncludedInFilterDefinition
  | KeywordSearchFilterDefinition;

export const isSimpleFilterDefinition = (
  definition: FilterDefinition
): definition is
  | DateFilterDefinition
  | QuickFilterDefinition
  | KeywordSearchFilterDefinition =>
  definition.type !== FilterDefinitionType.IS_INCLUDED_IN;

export interface SchemaDefinitionDefaultFilters {
  defaultFilter?: string;
  defaultAssetFilter?: string;
}

/**
 * Pipeline orchestration shared by smartGroupingConfig and aiSummaryConfig.
 * Tagged union: `runType` discriminator + a per-type config blob. Omitting the
 * field entirely means "manual / no orchestration". New run types add a string
 * + an inner config shape; the UI must extend the switch in
 * `tabs/shared/orchestration/`.
 */
export type SchemaConfigOrchestration =
  | { runType: "immediateOnConfigChange" }
  | {
      runType: "ingestionTrigger";
      config: { ingestionWfId: string };
    }
  | {
      runType: "cronTrigger";
      /**
       * `schedules` are cron expressions (standard crontab or Argo @-macros);
       * `timezone` is an IANA name (e.g. "America/Chicago"). Applied as an
       * Argo CronWorkflow.
       */
      config: { schedules: string[]; timezone: string };
    };

export interface SchemaAgglomerativeAlgorithmConfig {
  clusterEquivSimilarityThreshold?: number;
  phraseClusteringSimilarityThreshold?: number;
  minSamplesForClustering?: number;
  maxSamplesForClustering?: number;
}

export interface SchemaConsolidateAlgorithmConfig {
  newBatchSize?: number;
  canonicalShortlistSize?: number;
  numWorkers?: number;
  minClusterEventCount?: number;
}

/** Omitting `algorithm` entirely means agglomerative with backend defaults. */
export type SchemaConfigAlgorithm =
  | { type: "agglomerative"; config?: SchemaAgglomerativeAlgorithmConfig }
  | { type: "consolidate"; config?: SchemaConsolidateAlgorithmConfig };

export interface SchemaSmartGroupingConfig {
  textFields: string[];
  eventFilter?: string;
  /** WHERE-clause snippet derived from eventFilter by the backend on save. */
  eventFilterSql?: string;
  /** Name of the asset schema referenced by assetFilter / asset-related fields. */
  assetType?: string;
  assetFilter?: string;
  /** WHERE-clause snippet derived from assetFilter by the backend on save. */
  assetFilterSql?: string;
  nPods?: number;
  algorithm?: SchemaConfigAlgorithm;
  useAdvanceLlm?: boolean;
  llmModelSettings?: Record<string, unknown>;
  extractSingleSymptom?: boolean;
  /** Free-form knowledge items appended to the LLM context. Optional; absent = empty list. */
  addKnowledgeItems?: string[];
  /**
   * BCP 47 language tag (e.g. "en-US"). Optional — the backend applies no
   * default and omits it from responses when unset.
   */
  outputLanguage?: string;
  /**
   * Controls the cluster pipeline's reset behavior. When true (the backend
   * default when omitted) a run prunes smart groups no longer present in
   * ClickHouse and regenerates all descriptions; when false it only adds new
   * ones. Independent of `orchestration` — applies to every run regardless of
   * trigger.
   */
  reset?: boolean;
  orchestration?: SchemaConfigOrchestration;
}

/** The cost-relevant subset of a (draft) SchemaSmartGroupingConfig. */
export interface SchemaSmartGroupingCostEstimateRequest {
  textFields: string[];
  eventFilter?: string;
  assetType?: string;
  assetFilter?: string;
  useAdvanceLlm?: boolean;
}

export interface SchemaSmartGroupingCostEstimate {
  totalRowCount: number;
  distinctTextCount: number;
  estimatedCostUsd: number;
}

/**
 * AI-summary enrichment config. Stored in a dedicated top-level column (like
 * `smartGroupingConfig`), present only on event schemas with `hasAiSummary`
 * enabled. Send the object to set it, or null to clear it.
 */
export interface SchemaAISummaryConfig {
  /**
   * Event-table columns whose content is summarized. Required, at least one.
   * Any column type is allowed (the pipeline stringifies each); reserved /
   * auto-injected columns (e.g. `ai_summary`) and dotted relation references
   * are rejected by the backend.
   */
  columns: string[];
  /** Switch to the structured claim-summary prompt instead of free-text. */
  claim?: boolean;
  /** BCP 47 language tag (e.g. "ja-JP"). Omitted = pipeline default. */
  outputLanguage?: string;
  eventFilter?: string;
  /** WHERE-clause snippet derived from eventFilter by the backend on read. */
  eventFilterSql?: string;
  /**
   * Submit knob mirroring smartGroupingConfig — validated and persisted, but
   * not yet acted on by the backend. When true a run regenerates all summaries;
   * when false it only fills in missing ones.
   */
  reset?: boolean;
  /** Submit knob (1–15). Bounds the backfill's parallel pod fan-out. */
  nPods?: number;
  orchestration?: SchemaConfigOrchestration;
}

export type SchemaMatchedEventsDirection = "past" | "future" | "all";

/**
 * One matching rule on an event schema, describing how it is matched against a
 * single target event schema. Stored server-side in the dedicated
 * `matchedEventsConfig` object keyed by target schema name (see
 * `SchemaMatchedEventsConfig`), so the target name is the key rather than a
 * field here.
 */
export interface SchemaMatchedEventsTargetConfig {
  /** String columns on this (source) schema to compare. */
  sourceTextCols: string[];
  /** String columns on the target schema to compare. */
  targetTextCols: string[];
  /** UI filter query string applied to the source events. */
  sourceEventFilter?: string;
  /** WHERE-clause snippet derived from sourceEventFilter by the backend on read. */
  sourceEventFilterSql?: string;
  /** UI filter query string applied to the target events. */
  targetEventFilter?: string;
  /** WHERE-clause snippet derived from targetEventFilter by the backend on read. */
  targetEventFilterSql?: string;
  /**
   * On which side of the source event's date the matcher looks for target
   * events: past (on or before; the backend default when omitted), future (on
   * or after), or all (any date). A self-match can't use all.
   */
  direction?: SchemaMatchedEventsDirection;
  /** Free-form guidance passed to the matching job. */
  matchingInstructions?: string;
  /**
   * Controls this target's matching job reset behavior. When true (the
   * backend default when omitted) a run re-matches every event pair; when
   * false it only matches newly added events. Independent of `orchestration`
   * — applies to every run regardless of trigger.
   */
  reset?: boolean;
  orchestration?: SchemaConfigOrchestration;
}

/**
 * Matched-events configuration: an object keyed by target event-schema name.
 * Keying by target name mirrors how matched events are consumed downstream
 * (matchedEventCounts / the /matchedEvents/{target} endpoint), and makes
 * one-config-per-target structural rather than something to validate.
 */
export type SchemaMatchedEventsConfig = Record<
  string,
  SchemaMatchedEventsTargetConfig
>;

/**
 * Steers the LLM with the human labels previously given to events sharing the
 * same groupByColumn value. Requires the entry's humanLabelColumn.
 */
export interface SchemaGenericEnrichmentHistoricalPrior {
  /** Must be one of the entry's plain (non-relation) textFields. */
  groupByColumn: string;
  includeCounts?: boolean;
  restrictToPriors?: boolean;
}

/**
 * Shows each event its most similar human-labeled events as few-shot
 * examples. Requires the entry's humanLabelColumn.
 */
export interface SchemaGenericEnrichmentSimilarExamples {
  /** 1–25; the backend defaults to 5 when omitted. */
  numNeighbors?: number;
}

/** One prompt-driven LLM enrichment writing to a single existing output column. */
export interface SchemaGenericEnrichmentEntry {
  promptInstructions: string;
  outputDescription?: string;
  /**
   * Accepts the same two entry shapes as smartGroupingConfig.textFields — a
   * plain column on this event schema, or `<relationKey>.<column>` for a
   * string column of a many-to-one related schema.
   */
  textFields: string[];
  /** Only valid on a string/string_array output column. */
  allowedOutputLabels?: Record<string, string>;
  /** Only valid on a string_array output column; the backend defaults to 1 when omitted. */
  numOutputValues?: number;
  /**
   * Column holding the human-provided value for this output — must match the
   * output column's data type and is never sent to the LLM.
   */
  humanLabelColumn?: string;
  knowledgeItemCategory?: string;
  useAdvanceLlm?: boolean;
  /** 1–100; the backend defaults to 10 when omitted. */
  llmBatchSize?: number;
  /** Only valid on a string output column. */
  historicalPrior?: SchemaGenericEnrichmentHistoricalPrior;
  similarExamples?: SchemaGenericEnrichmentSimilarExamples;
  eventFilter?: string;
  /** Derived by the backend from eventFilter on read. */
  eventFilterSql?: string;
  /** Defaults to true (re-enrich every matching event) when omitted. */
  reset?: boolean;
  /** 1–15. */
  nPods?: number;
  orchestration?: SchemaConfigOrchestration;
}

/** Keyed by output column name — mirrors matchedEventsConfig's per-target keying. */
export type SchemaGenericEnrichmentConfig = Record<
  string,
  SchemaGenericEnrichmentEntry
>;

// ATTACHMENTS POLICY
//
// Mirrors the server's `config.attachments` contract
// (`schema_registry/v2/definitions/config_validation.go`). A schema with no
// `attachments` section is fully inert — no virtual count fields, no grouped
// read response, nothing to render. Declaring the first bucket implies
// enablement (see the Attachments tab); there is no standalone enable toggle.

export interface SchemaAttachmentBucket {
  name: string;
  /**
   * MIME allowlist narrowing this bucket's membership at read time. Each entry
   * is a literal MIME type (`application/pdf`) or a simple glob (`image/*`). An
   * empty/absent list means "no restriction" (accepts all types).
   */
  mimeTypes?: string[];
}

export interface SchemaAttachmentSlot {
  name: string;
  /** Name of the declared bucket this slot lives in. */
  bucket: string;
  /** Cardinality cap (1..1000). `max === 1` is a single-occupancy slot. */
  max: number;
  /** MIME allowlist for this slot; empty/absent inherits the bucket's list. */
  mimeTypes?: string[];
}

export interface SchemaAttachmentsConfig {
  /** Opt-in flag — only literal `true` activates the feature server-side. */
  enabled: boolean;
  /**
   * Cap on the number of attachments an entity may carry. Absent means
   * "unlimited"; when set it must be a positive integer (1..2147483647), so the
   * editor omits the key rather than persisting `0`.
   */
  maxFilesPerEntity?: number;
  /** Global MIME allowlist. Empty/absent = no restriction. */
  allowedMimeTypes?: string[];
  buckets: SchemaAttachmentBucket[];
  slots: SchemaAttachmentSlot[];
}

// ATTACHMENTS READ RESPONSE
//
// The shape the schema-data preload attaches to a record under `attachments`.
// These keys are snake_case because the server's `attachmentRowToMap` emits the
// raw file shape verbatim (unlike most v2 responses) — the render adapter in
// `attachmentsHelper.ts` remaps them to the camelCase `Photo`/`Attachment`
// shapes the v1 gallery/file-list components expect.

export interface SchemaAttachmentEntry {
  id: string;
  filename: string;
  content_type: string;
  storage_key: string;
  thumbnail_storage_key: string;
  created_at: string;
  // Presigned URLs are present only when the record was fetched with
  // `includeSrcURLs` (see U6) and the backend preload populated them.
  src_url?: string;
  src_thumbnail?: string;
  src_expire?: string;
}

/**
 * A single slot's value in the grouped response. Its shape depends on the
 * slot's `max` cardinality: `null`/object for `max === 1`, `[]`/array for
 * `max > 1` (see the server's `finalizeSlotShape`).
 */
export type SchemaSlotValue =
  SchemaAttachmentEntry | SchemaAttachmentEntry[] | null;

/**
 * The grouped attachments object: each declared bucket (plus the synthetic
 * `other`) is a top-level key holding an entry array, and `slots` groups the
 * per-slot values. The same endpoint may instead return a flat
 * `SchemaAttachmentEntry[]` when the schema declares no buckets/slots, so the
 * access helper tolerates both shapes (and an absent field).
 */
export interface GroupedSchemaAttachments {
  [bucketName: string]:
    SchemaAttachmentEntry[] | Record<string, SchemaSlotValue> | undefined;
  slots?: Record<string, SchemaSlotValue>;
}

/** Initial sort of tables listing this schema's records; `column` is a
 * sortable attribute accessor. A sort the user picks and persists wins over
 * it. */
export interface SchemaDefaultSort {
  column: string;
  direction: Sort;
}

export interface SchemaDefinitionConfig {
  enableListingPage?: boolean;
  enableMobileListingPage?: boolean;
  mobileDisplayName?: string;
  sidebarOrder?: number;
  // Absent or true keeps the data source searchable by the Duck AI assistant;
  // false hides it from Duck while keeping dashboards and listings working.
  duckEnabled?: boolean;
  iconSvg?: string;
  analyticsConfig?: SchemaAnalyticsConfig;
  dashboardViews?: SchemaDashboardView[];
  defaultFilters?: SchemaDefinitionDefaultFilters;
  defaultSort?: SchemaDefaultSort;
  detailsPage?: SchemaDetailsPageConfig;
  mobileDashboards?: SchemaMobileDashboardsConfig;
  mobileDetailsPage?: SchemaMobileDetailsPageConfig;
  mobileListingPage?: SchemaMobileListingPageConfig;
  smartGroupingConfig?: SchemaSmartGroupingConfig;
  /** Per-schema attachment policy (buckets/slots/MIME). Absent = inert. */
  attachments?: SchemaAttachmentsConfig;
  /**
   * Event-schema names whose matched-event counts render as "Related <X>"
   * columns on this event schema's listing page. Maps to the keys of the
   * `matchedEventCounts` map already returned by the v2 list endpoint.
   */
  matchedEventCountTypes?: string[];
  tableColumnAccessors?: string[];
}

export interface SchemaDefinition {
  ID: string;
  name: string;
  displayNameSingular: string;
  displayNamePlural: string;
  description: string | null;
  tableType: TableTypeEnum;
  smartGrouping?: boolean;
  hasAiSummary?: boolean;
  rowCount?: number;
  // When data last landed in the table, whether from a feed or an uploaded file. Null until something has.
  lastImportedAt?: string | null;
  origin: string;
  published: boolean;
  columns: Column[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  config: SchemaDefinitionConfig;
  /**
   * Stored in a dedicated column (not nested in `config`). Present only on
   * event schemas with smart grouping enabled; otherwise null/undefined.
   */
  smartGroupingConfig?: SchemaSmartGroupingConfig | null;
  /**
   * Dedicated column (not nested in `config`). Present only on event schemas
   * with AI summary enabled; otherwise null/undefined.
   */
  aiSummaryConfig?: SchemaAISummaryConfig | null;
  /**
   * Stored in a dedicated column (not inside `config`). Only set on event
   * schemas; null/undefined otherwise. Keyed by target schema name.
   */
  matchedEventsConfig?: SchemaMatchedEventsConfig | null;
  /** Same as matchedEventsConfig, but keyed by output column name. */
  genericEnrichmentConfig?: SchemaGenericEnrichmentConfig | null;
  relations?: SchemaRelation[];
}

export interface SchemaDefinitionColumnRequest {
  name: string;
  dataType: ColumnDataType;
  isPrimaryKey: boolean;
  isRequired: boolean;
  orderByPosition?: number;
  orderByExpr?: string;
  partitionPosition?: number;
  partitionExpr?: string;
  isLowCardinality?: boolean;
}

// What the create requests take for the table itself. Each adds its own columns.
export interface NewDataTableFields {
  name: string;
  displayNameSingular: string;
  displayNamePlural: string;
  description?: string;
  tableType: TableTypeEnum;
  // Whether the table shows up for the rest of the app once it exists.
  published: boolean;
}

export interface SchemaDefinitionCreateRequest {
  name: string;
  displayNameSingular: string;
  displayNamePlural: string;
  description?: string;
  tableType: TableTypeEnum;
  smartGrouping?: boolean;
  hasAiSummary?: boolean;
  columns: SchemaDefinitionColumnRequest[];
}

// Added columns are always nullable and cannot join PRIMARY KEY, ORDER BY, or PARTITION BY, so the endpoint takes
// only these fields.
export type AddSchemaDefinitionColumnRequest = Pick<
  SchemaDefinitionColumnRequest,
  "name" | "dataType" | "isLowCardinality"
>;

export interface AddSchemaDefinitionColumnsRequest {
  columns: AddSchemaDefinitionColumnRequest[];
}

export interface SchemaDefinitionUpdateRequest {
  published?: boolean;
  displayNameSingular?: string;
  displayNamePlural?: string;
  description?: string | null;
  config?: SchemaDefinitionConfig;
  /** Send the object to set it, or null to clear it. Omit to leave unchanged. */
  smartGroupingConfig?: SchemaSmartGroupingConfig | null;
  /** Send the object to set it, or null to clear it. Omit to leave unchanged. */
  aiSummaryConfig?: SchemaAISummaryConfig | null;
  /**
   * Top-level (dedicated column), not nested in `config`. Send `null` to clear
   * it; omit to leave it untouched (PATCH semantics).
   */
  matchedEventsConfig?: SchemaMatchedEventsConfig | null;
  /** Same null/omit semantics as matchedEventsConfig. */
  genericEnrichmentConfig?: SchemaGenericEnrichmentConfig | null;
}

export interface ListSchemaDefinitionsRequest extends APIPaginatedRequest {
  skipRequest?: boolean;
}

export interface RelationColumnPair {
  ID: string;
  relationID: string;
  columnID: string;
  column?: Column;
  relatedColumnID: string;
  relatedColumn?: Column;
  createdAt: string;
}

export interface SchemaRelation {
  ID: string;
  schemaID: string;
  schema?: SchemaDefinition;
  relatedSchemaID: string;
  relatedSchema?: SchemaDefinition;
  key: string;
  isOneToMany: boolean;
  createdAt: string;
  updatedAt: string;
  columnPairs: RelationColumnPair[];
}

export interface ListSchemaRelationsRequest extends APIPaginatedRequest {}

export interface CreateSchemaRelationRequest {
  schemaName: string;
  relatedSchemaName: string;
  key: string;
  isOneToMany: boolean;
  columnPairs: { columnName: string; relatedColumnName: string }[];
}

// imageData is base64-encoded bytes (Go marshals []byte as base64 over JSON).
// The list endpoint omits it to keep responses small.
export interface SchemaImage {
  ID: string;
  schemaDefinitionID: string;
  imageOrder: number;
  matchRules: Record<string, unknown>;
  imageData?: string;
  contentType: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface CreateSchemaImageRequest {
  imageOrder: number;
  matchRules: Record<string, unknown>;
  imageData: string;
}

export interface UpdateSchemaImageRequest {
  imageOrder?: number;
  matchRules?: Record<string, unknown>;
  imageData?: string;
}

// ATTACHMENTS BULK IMPORT

export interface AttachmentImportFileRequest {
  index: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  keys: Record<string, string>;
  slot?: string;
}

export type AttachmentImportErrorReason =
  | "missing_key"
  | "invalid_key"
  | "record_not_found"
  | "content_type_not_allowed"
  | "slot_unknown"
  | "slot_full"
  | "duplicate"
  | "invalid_filename"
  | "empty_file"
  | "file_too_large"
  | "too_many_files";

interface AttachmentImportFileOkBase {
  index: number;
  status: "ok";
  entityID: string;
  storageKey: string;
  uploadURL: string;
}

interface AttachmentThumbnailUpload {
  thumbnailStorageKey: string;
  /** The type the thumbnail URL was signed with; the blob must be encoded in it. */
  thumbnailContentType: string;
  thumbnailUploadURL: string;
}

// The server emits the three thumbnail fields together or not at all; the type
// encodes that pairing.
export type AttachmentImportFileOk = AttachmentImportFileOkBase &
  (
    | AttachmentThumbnailUpload
    | Partial<Record<keyof AttachmentThumbnailUpload, never>>
  );

export interface AttachmentImportFileError {
  index: number;
  status: "error";
  reason: AttachmentImportErrorReason;
  message: string;
}

export type AttachmentImportFileResult =
  AttachmentImportFileOk | AttachmentImportFileError;

export interface AttachmentImportResponse {
  entityType: string;
  expiresAt: string;
  files: AttachmentImportFileResult[];
}

// ATTACHMENTS RECORD UPLOAD

export interface SchemaRecordRef {
  tableType: string;
  name: string;
  id: string;
}

export interface RecordAttachmentPresignRequest {
  index: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  slot?: string;
  replace?: boolean;
}

export interface RecordAttachmentPresignResponse extends AttachmentImportResponse {
  entityID: string;
}

// The endpoint returns the v1 attachment File model, plus the presigned src-URL
// fields (sent only with includeSrcURLs) that Photo also carries.
export type SchemaRecordAttachmentFile = Attachment &
  Pick<Photo, "srcExpire" | "srcThumbnail" | "srcUrl">;

export interface CreateSchemaRecordAttachmentRequest {
  storageKey: string;
  filename: string;
  contentType: string;
  slot?: string;
  replace?: boolean;
}
